/*
 * -----------------------------------------------------------------------
 *  <copyright file="unassign-library-control.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogActions } from '@mui/material';
import { pick } from 'lodash';

import { LibraryRiskOrgUpdate } from '@pages/admin/admin-risks/library-risk-update.interface';
import Button from '../../core/buttons/button';
import { CustomDialog, DialogContent, DialogTitle } from '../../core/dialog';

import { useBoolean, useTranslation } from 'hooks';
import {
    getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_libraryControls_LibraryControl,
    getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk,
} from '@queries/generated/legacy-risk-assessment.generated';
import { GET_COMPLETE_RISK, UPDATE_LIBRARY_RISK, GET_LIBRARY_CONTROLS } from '@queries/legacy-risk-assessment';

import './styles.scss';

const UnassignLibraryControl = ({
    open,
    onClose,
    currentRisk,
    currentControl,
    organisationId,
    isRisk,
}: {
    open: boolean;
    onClose: (action: string | React.MouseEvent) => void;
    currentRisk: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk;
    currentControl: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_libraryControls_LibraryControl;
    organisationId: string;
    isRisk: boolean;
}) => {
    const { t } = useTranslation();
    const onLoading = useBoolean(false);
    const [saveRisk] = useMutation(UPDATE_LIBRARY_RISK);

    const close = (action: string | React.MouseEvent) => {
        onClose(action);
    };

    const unAssociateControl = () => {
        if (!currentRisk) return;

        onLoading.setTrue();
        let ids = currentRisk.libraryControlIds as string[];
        ids = ids?.filter((id) => id !== currentControl.id);

        const newRisk = pick(currentRisk, LibraryRiskOrgUpdate);

        saveRisk({
            variables: {
                risk: {
                    ...newRisk,
                    libraryControlIds: ids,
                    organisationId,
                },
            },
            refetchQueries: [
                {
                    query: GET_COMPLETE_RISK,
                    variables: {
                        id: currentRisk.id,
                    },
                },
                {
                    query: GET_LIBRARY_CONTROLS,
                    variables: {
                        organisationId,
                        includeSystemControls: Boolean(!organisationId),
                    },
                },
            ],
        })
            .then(() => {
                onLoading.setFalse();
                close('success');
            })
            .catch(() => {
                onLoading.setFalse();
                close('error');
            });
    };

    if (!open) return null;

    return (
        <CustomDialog onClose={close} fullWidth={false} maxWidth="md" scroll="paper" open>
            <DialogTitle className="unassign-library-control-modal-title">
                {isRisk ? t('ra:unassignRisk') : t('ra:unassignControl')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={close}
                    data-testid="close-dialog"
                />
            </DialogTitle>

            <DialogContent className="unassign-control-from-risk-dialog-content">
                <p className="sub-title">{t('ra:confirmUnassignControlFromRisk')}</p>
                {!isRisk ? (
                    <>
                        <div className="block">
                            <p className="block-title">{t('ra:riskStatement')}</p>
                            <p>{currentRisk && currentRisk.statement}</p>
                        </div>

                        <div className="block">
                            <p className="block-title">
                                {t('ra:control')} {t('description')}
                            </p>
                            <p>{currentControl && currentControl.description}</p>
                        </div>
                    </>
                ) : null}
                {isRisk ? (
                    <>
                        <div className="block">
                            <p className="block-title">
                                {t('ra:control')} {t('description')}
                            </p>
                            <p>{currentControl && currentControl.description}</p>
                        </div>
                        <div className="block">
                            <p className="block-title">{t('ra:riskStatement')}</p>
                            <p>{currentRisk && currentRisk.statement}</p>
                        </div>
                    </>
                ) : null}
            </DialogContent>

            <DialogActions className="delete-cv-actions">
                <Button disabled={onLoading.value} onClick={close} text="cancel" />
                <Button disabled={onLoading.value} text="unassign" onClick={unAssociateControl} />
            </DialogActions>
        </CustomDialog>
    );
};

export default UnassignLibraryControl;
